<template>
  <div class="inputstring-component" v-click-away="setUnclickedState" @click="setClickedState">
    <IslandBadge type="inputstring" />
    <div class="component-contents">
      <div class="message-part">
        <TextEditor
          ref="textEditor"
          :value="state.text"
          :placeholder="'Enter a question...'"
          @text-updated="textUpdated"
          @error="setInvalidAttribute"
        ></TextEditor>
        <p v-if="isTextRequiredInvalid" class="help is-danger">Message text must not be empty</p>
        <p v-if="isTextAttributeInvalid" class="help is-danger">Message contains an invalid attribute</p>
      </div>
      <div class="response-part">
        <VariablePicker :value="state.storedVariable" @variable-selected="variableSelected" />
        <p v-if="isVariableRequiredInvalid" class="help is-danger">Must store result in a variable</p>
        <p v-if="isAttributeMissingInvalid" class="help is-danger">Attribute does not exist</p>

        <p v-if="isAttributeArchivedInvalid" class="help is-danger">Attribute has been archived</p>
      </div>
    </div>

    <AdditionalInformationWrapper v-if="showConfig && visibleFields.length" color="#FF6666" text-color="#FFFFFF">
      <template v-slot:content>
        <InputValidationConfig
          :value="state"
          :fields="visibleFields"
          @updated="additionalConfigChanged"
        ></InputValidationConfig>
      </template>
    </AdditionalInformationWrapper>

    <transition :name="transition">
      <div v-if="hasClicked && !showConfig && visibleFields.length" class="expander" @click="showExpandedConfig">
        <ChevronRight class="chevron"> </ChevronRight>
      </div>
    </transition>

    <div v-if="hasClicked && showConfig && visibleFields.length" class="expander" @click="hideExpandedConfig">
      <ChevronLeft class="chevron"> </ChevronLeft>
    </div>

    <div
      class="invalid-warning"
      :class="{ 'shift-for-expander': hasClicked && visibleFields.length }"
      v-if="state.invalid && !showConfig"
    >
      <ErrorIcon class="icon-1-3x"></ErrorIcon>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { InputVariableViewModel, StateViewModel } from '@/features/bots/models/designer';
import VariablePicker from './VariablePicker/VariablePicker.vue';
import IslandBadge from './IslandBadge.vue';
import AdditionalInformationWrapper from './AdditionalInformationWrapper.vue';
import InputValidationConfig from './InputValidationConfig.vue';
import { required } from '@vuelidate/validators';
import ChevronRight from 'vue-material-design-icons/ChevronDoubleRight.vue';
import ChevronLeft from 'vue-material-design-icons/ChevronDoubleLeft.vue';
import ErrorIcon from 'vue-material-design-icons/AlertCircleOutline.vue';
import TextEditor from '@/features/bots/components/designer/TextEditor/TextEditor.vue';
import { TransitionType } from '@/features/bots/models/designer/transitionType';
import { RegexType } from '../../models/designer/regexType';
import { computed, nextTick, onMounted, PropType, ref, watch } from 'vue';
import { useStore } from 'vuex';
import useVuelidate from '@vuelidate/core';
import VuelidateUtil from '@shared/util/vuelidateUtil';

const props = defineProps({
  value: {
    type: Object as PropType<StateViewModel>,
    required: true,
  },
});

const emit = defineEmits<{
  (e: 'state-updated', value: StateViewModel): void;
}>();

defineExpose({
  focusOnInput,
});

const store = useStore();
const doesAttributeExist = (attributeId: string) => computed(() => store.getters.doesAttributeExist(attributeId));
const isAttributeArchived = (attributeId: string) => computed(() => store.getters.isAttributeArchived(attributeId));
const textEditor = ref<typeof TextEditor | null>(null);

const showConfig = ref(false);
const hasClicked = ref(false);
const transition = ref('display');
const invalidAttribute = ref(false);
const state = ref<StateViewModel>({
  id: '',
  component: 'InputString',
});
const visibleFields = ref<string[]>([]);
const selectedVariable = ref('');
const additionalConfigInvalid = ref(false);

const validations = {
  state: {
    text: {
      required,
      attributeInvalid(text: string) {
        return !invalidAttribute.value;
      },
    },
  },
  selectedVariable: {
    required,
    attributeMissing(value: string) {
      const storedVar = state.value.storedVariable;
      return !storedVar || storedVar.scope !== 'appUser.customAttributes' || doesAttributeExist(storedVar.key);
    },
    attributeArchived(value: string) {
      return !isArchived();
    },
  },
};

const $v = useVuelidate(validations, { state, selectedVariable });
const isTextRequiredInvalid = computed(() => VuelidateUtil.isRequiredInvalid($v, (v) => v.state.text));
const isTextAttributeInvalid = computed(() =>
  VuelidateUtil.isValidationInvalid($v, (v) => v.state.text, 'attributeInvalid'),
);
const isVariableRequiredInvalid = computed(() => VuelidateUtil.isRequiredInvalid($v, 'selectedVariable'));
const isAttributeMissingInvalid = computed(() =>
  VuelidateUtil.isValidationInvalid($v, 'selectedVariable', 'attributeMissing'),
);
const isAttributeArchivedInvalid = computed(() =>
  VuelidateUtil.isValidationInvalid($v, 'selectedVariable', 'attributeArchived'),
);

watch(
  () => props.value,
  (value: StateViewModel) => {
    try {
      state.value = value;
      if (state.value.storedVariable) {
        selectedVariable.value = state.value.storedVariable.id;
      }
      updateValidation();
    } catch (err: any) {
      console.error('watch(() => props.value', err);
    }
  },
  { immediate: true },
);

onMounted(() => initialiseVisibleFields());

function setClickedState() {
  hasClicked.value = true;
}

function setUnclickedState() {
  hasClicked.value = false;
  showConfig.value = false;
  transition.value = 'display';

  $v.value.$touch();
}

function showExpandedConfig() {
  hasClicked.value = true;
  showConfig.value = true;
  transition.value = 'none';
}

function hideExpandedConfig() {
  showConfig.value = false;
}

function textUpdated(text: string) {
  if (state.value.text === text) {
    return;
  }

  state.value.text = text;

  updateValidation();
  if ($v.value.state && $v.value.state.text) {
    $v.value.state.text.$touch();
  }

  emit('state-updated', state.value);
}

function initialiseVisibleFields() {
  if (isAppUserName()) {
    visibleFields.value = [];
  } else if (isAppUserEmail()) {
    visibleFields.value = ['errorMessage', 'allowedAttempts', 'noMatch'];
  } else if (isAppUserPhoneNumber()) {
    visibleFields.value = ['errorMessage', 'allowedAttempts', 'noMatch'];
  } else {
    visibleFields.value = ['errorMessage', 'pattern', 'allowedAttempts', 'noMatch'];
  }
}

function variableSelected(variable: InputVariableViewModel) {
  selectedVariable.value = variable.id;
  state.value.storedVariable = variable;

  if (isAppUserName()) {
    state.value.pattern = '';
    state.value.allowedAttempts = -1;
    state.value.errorMessage = '';
    state.value.noMatch = { type: TransitionType.Path, payload: '' };
    additionalConfigInvalid.value = false;
  } else if (isAppUserEmail()) {
    state.value.pattern = RegexType.EMAIL;
    state.value.allowedAttempts = -1;
    state.value.errorMessage =
      state.value.errorMessage && state.value.errorMessage.length
        ? state.value.errorMessage
        : "That's not a valid email address, please try again.";
    state.value.noMatch = { type: TransitionType.Path, payload: '' };
    additionalConfigInvalid.value = false;
  } else if (isAppUserPhoneNumber()) {
    state.value.pattern = RegexType.PHONE_NUMBER;
    state.value.allowedAttempts = -1;
    state.value.errorMessage =
      state.value.errorMessage && state.value.errorMessage.length
        ? state.value.errorMessage
        : "That's not a valid phone number, please try again.";
    state.value.noMatch = { type: TransitionType.Path, payload: '' };
    additionalConfigInvalid.value = false;
  } else if (state.value.storedVariable.dataType === 'number') {
    state.value.pattern = RegexType.NUMERIC;
    state.value.allowedAttempts = -1;
    state.value.errorMessage =
      state.value.errorMessage && state.value.errorMessage.length
        ? state.value.errorMessage
        : "That's not a valid number, please try again.";
    state.value.noMatch = { type: TransitionType.Path, payload: '' };
    additionalConfigInvalid.value = false;
  } else if (state.value.storedVariable.dataType === 'decimal') {
    state.value.pattern = RegexType.DECIMAL;
    state.value.allowedAttempts = -1;
    state.value.errorMessage =
      state.value.errorMessage && state.value.errorMessage.length
        ? state.value.errorMessage
        : "That's not a valid decimal, please try again.";
    state.value.noMatch = { type: TransitionType.Path, payload: '' };
    additionalConfigInvalid.value = false;
  } else {
    state.value.pattern = '';
    state.value.allowedAttempts = -1;
    state.value.errorMessage =
      state.value.errorMessage && state.value.errorMessage.length ? state.value.errorMessage : '';
    state.value.noMatch =
      state.value.noMatch && state.value.noMatch.payload
        ? state.value.noMatch
        : { type: TransitionType.Path, payload: '' };
  }

  initialiseVisibleFields();
  if ($v.value.state && $v.value.state.storedVariable) {
    $v.value.state.storedVariable.$touch();
  }
  updateValidation();

  emit('state-updated', state.value);
}

function additionalConfigChanged(newState: StateViewModel, invalid: boolean) {
  state.value = newState;
  additionalConfigInvalid.value = invalid;
  updateValidation();
  emit('state-updated', state.value);
}

function isArchived(): boolean {
  if (state.value && state.value.storedVariable) {
    return isAttributeArchived(state.value.storedVariable!.key).value;
  }
  return false;
}

function setInvalidAttribute(val: boolean) {
  invalidAttribute.value = val;
  $v.value.$touch();
  state.value.invalid = $v.value.$invalid;
}

function focusOnInput() {
  textEditor.value?.focusElement();
  textEditor.value?.startEditing();
}

function isAppUserName() {
  return (
    state.value.storedVariable &&
    state.value.storedVariable.scope === 'appUser' &&
    state.value.storedVariable.key === 'name'
  );
}

function isAppUserEmail() {
  return (
    state.value.storedVariable &&
    state.value.storedVariable.scope === 'appUser' &&
    state.value.storedVariable.key === 'email'
  );
}

function isAppUserPhoneNumber() {
  return (
    state.value.storedVariable &&
    state.value.storedVariable.scope === 'appUser' &&
    state.value.storedVariable.key === 'phoneNumber'
  );
}

async function updateValidation() {
  if (!state.value) return;
  await nextTick(); // Needed so variable picker if opened unmounts otherwise we will pickup the validation
  state.value.invalid = additionalConfigInvalid.value || $v.value.$invalid;
}
</script>

<style scoped lang="scss">
.inputstring-component {
  display: flex;
  flex: 1;
}

.component-contents {
  flex-direction: column;
}

.message-part {
  padding-right: 1em;
}

.response-part {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  margin-top: 10px;

  .button {
    background-color: transparent;
    border: none;
  }
}

.dropdown-divider {
  background-color: #ededed;
  border: none;
  display: block;
  height: 1px;
  margin: 0.5rem 0;
}

.expander {
  position: absolute;
  width: 40px;
  height: 100%;
  left: 100%;
  top: 0;

  color: white;

  border-radius: 0 5px 5px 0;
  background-color: $color-netcall-salmon;
  display: flex;
  justify-content: center; /* align horizontal */
  align-items: center;
}

.chevron {
  font-size: 35px;
  color: white;
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;

  svg {
    top: 50%;
    transform: translateY(-50%);
    -ms-transform: translateY(-50%);
    -moz-transform: translateY(-50%);
    -webkit-transform: translateY(-50%);
    -o-transform: translateY(-50%);
  }

  &:hover {
    cursor: pointer;
  }
}

.display-enter-active {
  transition: width 0.25s;
}

.display-leave-active {
  transition: width 0.25s;
}

.display-enter,
.display-leave-to {
  width: 10px;
}

.display-enter-to,
.display-leave {
  width: 40px;
}
</style>
